/**
 * Pure tRPC client configuration helpers.
 *
 * The `createTrpcClient` factory ([client.ts](./client.ts)) composes
 * the runtime `httpBatchLink` against these pure helpers. Splitting the
 * URL / header composition out keeps the unit tests free of `fetch` /
 * `AbortController` plumbing: given a base URL + an optional Clerk
 * token, produce the tRPC URL and the request headers.
 *
 * Auth is single-header `Authorization: Bearer <token>`, matching the
 * API's `joseClerkJwtVerifier` (which reads the `authorization`
 * header). When the token is absent the header is simply omitted; the
 * support procedures are staff-gated server-side so an anonymous /
 * non-staff caller gets UNAUTHORIZED / FORBIDDEN regardless.
 */

export const buildTrpcUrl = (apiBaseUrl: string): string => {
  // Strip any trailing slashes so we don't double-up when joining.
  const trimmed = apiBaseUrl.replace(/\/+$/, "");
  return `${trimmed}/trpc`;
};

export type TrpcRequestHeaders = Readonly<Record<string, string>>;

export const buildAuthHeaders = (
  token: string | undefined
): TrpcRequestHeaders => {
  if (token === undefined) return {};
  const trimmed = token.trim();
  if (trimmed.length === 0) return {};
  return { Authorization: `Bearer ${trimmed}` };
};
